'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { AUTH_PROVIDER } from '@/constants/auth';
import { Slide } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { COLOR_SCHEME } from '@/constants/colorScheme';
import { useAdminGuard } from '@/hooks/useAdminGuard';
import { usePrivate } from '@/components/private';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  styled,
  Toolbar,
} from '@/primitives';
import { UserAvatar } from '@/components/avatars/UserAvatar';
import {
  APP_ROUTE_NAMES,
  APP_ROUTE_PATHS,
  PUBLIC_ROUTES,
} from '@/configs/routes';
import { useIsPublicSearch } from '@/hooks/useIsPublicSearch';
import { useIsRegistryRoute } from '@/hooks/useIsRegistryRoute';
import {
  LAYOUT_VARIANT,
  type LayoutVariant,
  NAV_HEIGHT,
} from '@/configs/layout';
import SocietySearchHeaderCenterDesktop from '@/components/publicSearch/searchPage/SocietySearchHeaderCenterDesktop';
import SocietySearchHeaderLogo from '@/components/publicSearch/searchPage/SocietySearchHeaderLogo';
import { useHeaderEntranceOnce } from './useHeaderEntranceOnce';
const HEADER_TRANSITION_MS = 280;

const LinkStyled = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
`;

const navLinkStackSx = {
  flexDirection: { xs: 'column', md: 'row' },
  alignItems: { md: 'center' },
  gap: { xs: '2px', md: '4px' },
  width: { xs: '100%', md: 'fit-content' },
} as const;

/** Shared nav-link spec — must stay in sync with `textNavSx` in
 *  HomeFloatingChrome (same metrics, theme tokens instead of the landing's
 *  over-hero slate palette) so `/` ⇄ other routes doesn't restyle the links. */
const navLinkSx = (active: boolean) =>
  ({
    position: 'relative',
    justifyContent: { xs: 'flex-start', md: 'center' },
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.9375rem',
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    width: 'fit-content',
    minWidth: 0,
    minHeight: 44,
    px: 1.25,
    py: 0,
    borderRadius: '8px',
    color: active ? 'primary.main' : 'text.secondary',
    backgroundColor: 'transparent',
    // The active marker is drawn relative to the label rather than to the
    // box, so growing the box to a 44px touch target doesn't strand the
    // underline halfway down the header.
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 10,
      right: 10,
      top: 'calc(50% + 13px)',
      height: 2,
      borderRadius: 1,
      backgroundColor: active ? 'primary.main' : 'transparent',
    },
    '&:hover': {
      backgroundColor: 'transparent',
      color: active ? 'primary.main' : 'text.primary',
    },
  }) as const;

interface Props {
  /** Passed by the toolbar wrapper — avoids useMediaQuery SSR default (false)
      flashing mobile nav links left-aligned inside the desktop grid. */
  layout?: LayoutVariant;
  onNavigate?: () => void;
  onClose?: () => void;
}

function HeaderNavLinkStack({
  pathname,
  isPublicSearch,
  isRegistryRoute,
  onNavigate,
}: {
  pathname: string;
  isPublicSearch: boolean;
  isRegistryRoute: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Stack direction="row" sx={navLinkStackSx}>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.HOME}
        sx={navLinkSx(pathname === PUBLIC_ROUTES.HOME)}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.HOME}
      </Button>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.SEARCH}
        sx={navLinkSx(pathname === PUBLIC_ROUTES.SEARCH || isPublicSearch)}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.SEARCH}
      </Button>
      <Button
        component={LinkStyled}
        href={PUBLIC_ROUTES.REGISTRY_LANDING}
        sx={navLinkSx(
          // `useIsRegistryRoute` covers the signed-in registry app only —
          // the public `/registry` landing page needs its own check.
          isRegistryRoute || pathname === PUBLIC_ROUTES.REGISTRY_LANDING,
        )}
        onClick={onNavigate}
      >
        {APP_ROUTE_NAMES.REGISTRY}
      </Button>
    </Stack>
  );
}

export default function Navigation({
  layout = LAYOUT_VARIANT.MOBILE,
  onNavigate,
  onClose,
}: Props) {
  const { resolvedScheme } = useResolvedColorScheme();
  const entranceAnimation = useHeaderEntranceOnce();
  const isMdUp = layout === LAYOUT_VARIANT.DESKTOP;
  const isPublicSearch = useIsPublicSearch();
  const isRegistryRoute = useIsRegistryRoute();
  const pathname = usePathname();
  const { isAuthenticated, isAuthLoading } = useAdminGuard(
    APP_ROUTE_PATHS.DASHBOARD,
  );
  const { userProfile, userProfileError } = usePrivate();
  // After auth resolves, there's a second request that fetches the user
  // profile (PostLoginFlow → /api/UserProfile/:id). Until that lands the
  // avatar has no name or image, so show the spinner during that window
  // too — not just during the session check. If that request fails
  // (`userProfileError`) we stop waiting and render the avatar in a degraded
  // state (email fallback) so the menu/logout stay reachable instead of the
  // spinner hanging forever.
  const isAuthenticatedAwaitingProfile =
    isAuthenticated && !userProfile && !userProfileError;

  const centerSlideKey = isPublicSearch
    ? 'header-center-society'
    : 'header-center-default';

  const breedlogicLogoSrc =
    resolvedScheme === COLOR_SCHEME.DARK
      ? '/breedlogic-logo-dark.svg'
      : '/breedlogic-logo.svg';
  return (
    <Toolbar
      component="nav"
      disableGutters
      sx={{
        flexDirection: { xs: 'column', md: 'row' },
        display: { xs: 'flex', md: 'grid' },
        // `auto minmax(0, 1fr) auto`: the flanking columns size to their own
        // content (logo + society logo on the left, auth slot on the right) so
        // they're never encroached on, and the centre column takes the
        // remaining space as a *definite* width. A content-sized `auto` centre
        // (the previous `1fr auto 1fr`) let the tabs grow until they overlapped
        // the society logo, and starved the tab row's overflow logic — it
        // measures the centre column to decide how many tabs fit, so a
        // content-sized column always reported "everything fits" and the "More"
        // menu never engaged. A flexible centre clips via overflow:hidden and
        // gives that logic the true available width.
        gridTemplateColumns: { md: 'auto minmax(0, 1fr) auto' },
        alignItems: { xs: 'start', md: 'center' },
        columnGap: { md: '16px' },
        gap: { xs: '10px', md: 0 },
        px: { xs: 0, md: '24px' },
        py: { md: 0 },
        width: { md: '100%' },
        height: { md: '100%' },
        minHeight: { xs: 'auto', md: `${NAV_HEIGHT}px` },
        boxSizing: 'border-box',
        // Pure-CSS entrance — slides + fades the nav contents in from below
        // on first paint. The surrounding AppBar (background/border) stays
        // static and clips this with overflow:hidden so the contents appear
        // to rise into the bar from beneath it. First full page load only —
        // undefined on later mounts (client-side navigation).
        animation: entranceAnimation,
      }}
    >
      {isMdUp ? (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: '12px',
            flexShrink: 0,
            minWidth: 0,
            justifySelf: 'start',
          }}
        >
          <LinkStyled
            href={PUBLIC_ROUTES.HOME}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              width: 112,
              height: 36,
              padding: 0,
            }}
          >
            <Image
              alt="BreedLOGIC Logo"
              height={37}
              width={176}
              priority
              src={breedlogicLogoSrc}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              }}
            />
          </LinkStyled>
          {isPublicSearch ? <SocietySearchHeaderLogo /> : null}
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <LinkStyled
            href={PUBLIC_ROUTES.HOME}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              width: 96,
              height: 30,
              padding: 0,
            }}
          >
            <Image
              alt="BreedLOGIC Logo"
              height={37}
              width={176}
              priority
              src={breedlogicLogoSrc}
              style={{
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              }}
            />
          </LinkStyled>
          {onClose && (
            <IconButton aria-label="Close menu" size="small" onClick={onClose}>
              <CloseIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
            </IconButton>
          )}
        </Box>
      )}
      {isMdUp ? (
        <Box
          sx={{
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '4px',
            overflow: 'hidden',
          }}
        >
          {/* `appear={false}` is essential here — MUI Slide defaults appear
              to true. With it on, SSR rendered the menu in its final position
              and JS then snapped it off-screen before animating in (visible as
              a flash). With appear={false}, the entrance is omitted on first
              mount; the transition still plays on key changes (header swaps). */}
          <Slide
            appear={false}
            direction={isPublicSearch ? 'down' : 'up'}
            timeout={HEADER_TRANSITION_MS}
            in
            key={centerSlideKey}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {isPublicSearch ? (
                <SocietySearchHeaderCenterDesktop />
              ) : (
                <HeaderNavLinkStack
                  pathname={pathname}
                  isPublicSearch={isPublicSearch}
                  isRegistryRoute={isRegistryRoute}
                  onNavigate={onNavigate}
                />
              )}
            </Box>
          </Slide>
        </Box>
      ) : (
        <Slide
          appear={false}
          direction={isPublicSearch ? 'down' : 'up'}
          timeout={HEADER_TRANSITION_MS}
          in
          key={
            isPublicSearch ? 'header-mobile-society' : 'header-mobile-default'
          }
        >
          {/* order 2 pushes the nav links below the auth/profile slot in the
              drawer's column — the user pill sits directly under the logo
              row, links follow. */}
          <Box sx={{ width: '100%', order: 2 }}>
            <HeaderNavLinkStack
              pathname={pathname}
              isPublicSearch={isPublicSearch}
              isRegistryRoute={isRegistryRoute}
              onNavigate={onNavigate}
            />
          </Box>
        </Slide>
      )}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: { md: 'flex-end' },
          gap: { xs: 1, md: 1.5 },
          flexShrink: 0,
          width: { xs: '100%', md: 'auto' },
          justifySelf: { md: 'end' },
          // Drawer column: sit between the logo row (0) and the nav links
          // (2). md is a grid where order would also apply — reset it.
          order: { xs: 1, md: 0 },
        }}
      >
        {/* Three states for the auth/profile slot:
            1. `isAuthLoading` — session is still being resolved. Empty
               placeholder so we don't briefly flash a Sign-in button at a
               user who turns out to be authenticated.
            2. `isAuthenticatedAwaitingProfile` — session resolved, but
               PostLoginFlow's profile request hasn't landed yet, so the
               avatar would render with no name or image. Show a spinner.
            3. Settled: either UserAvatar (with name/image) or the Sign-in
               button. The loading states reserve the sign-in button's
               73×40 footprint (mirroring HomeFloatingChrome) so the right
               edge doesn't jump while auth resolves. */}
        {isAuthLoading && (
          <Box
            aria-hidden
            sx={{
              minHeight: { xs: '32px', md: '40px' },
              height: { xs: '32px', md: '40px' },
              width: { xs: '100%', md: '73px' },
              flexShrink: 0,
            }}
          />
        )}
        {!isAuthLoading && isAuthenticatedAwaitingProfile && (
          <Box
            aria-label="Loading user profile"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: { xs: '32px', md: '40px' },
              height: { xs: '32px', md: '40px' },
              width: { xs: '100%', md: '73px' },
              flexShrink: 0,
            }}
          >
            <CircularProgress size={18} thickness={4} />
          </Box>
        )}
        {/* Rounded pill matches the landing header's signed-in treatment
            (HomeFloatingChrome `authPillSx`) — same padding, content-sized
            width and 148px name cap, so the pill is pixel-identical across
            `/` and every other route. */}
        {!isAuthLoading &&
          !isAuthenticatedAwaitingProfile &&
          isAuthenticated && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '999px',
                padding: { xs: '4px 6px', md: '4px 6px 4px 4px' },
                width: { xs: '100%', md: 'auto' },
                minWidth: 0,
                minHeight: { md: 36 },
                transition:
                  'background-color 0.2s ease, border-color 0.2s ease, border-radius 0.2s ease',
                // In the drawer the avatar expands its menu inline, inside
                // this pill — a 999px radius around that tall panel renders
                // as an oval. Square the corners off while it's open.
                '&:has(#user-profile-button[aria-expanded="true"])': {
                  borderRadius: { xs: '12px', md: '999px' },
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '& #user-profile-button': { gap: '4px' },
                '& .MuiTypography-root': {
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 148,
                },
              }}
            >
              <UserAvatar
                compact
                inline={!isMdUp}
                trailingIcon={isMdUp ? 'chevron' : 'menu'}
              />
            </Box>
          )}
        {!isAuthLoading &&
          !isAuthenticatedAwaitingProfile &&
          !isAuthenticated && (
            <Button
              sx={{
                minHeight: { xs: '32px', md: '40px' },
                height: { xs: '32px', md: '40px' },
                width: { xs: '100%', md: 'auto' },
                fontSize: '14px',
                fontWeight: 600,
                py: { xs: 0, md: undefined },
                px: 1.75,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
              variant="contained"
              onClick={() => {
                void signIn(AUTH_PROVIDER, { callbackUrl: pathname });
                onNavigate?.();
              }}
            >
              Sign in
            </Button>
          )}
      </Stack>
    </Toolbar>
  );
}
